// src/utils/excel/excelExporter.js
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { ESTATUS } from '../../config/config';

// === Config de auto-ancho ===
const COL_MARGIN = 5;  // Margen extra para que no quede muy justo

// === Estilo por estatus (pinta fila completa) ===
// 1 → Inventario (azul pastel), 6 → Facturado no entregado (morado pastel)
function getStatusExcelStyle(estatus) {
  if (estatus === ESTATUS.INVENTARIO) {
    return {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0F2FE' } }, // azul muy suave
      font: { color: { argb: 'FF1D4ED8' }, bold: true }, // solo lo uso para la celda "estatus"
      align: { horizontal: 'center' },
    };
  }
  if (estatus === ESTATUS.FACTURADO_NO_ENTREGADO) {
    return {
      fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEDE9FE' } }, // morado suave
      font: { color: { argb: 'FF6D28D9' }, bold: true },
      align: { horizontal: 'center' },
    };
  }
  return null;
}

/**
 * Exporta rows -> Excel usando un schema y un set de columnas visibles (ordenadas).
 * @param {Object} cfg
 * @param {Array<Object>} cfg.rows - Datos crudos (array de objetos)
 * @param {Array<string>} cfg.visibleColumns - Keys en el orden a exportar
 * @param {Object} cfg.schema - {key: {header, type?, value?(row)->any}}
 * @param {string} cfg.filename - Nombre del archivo (sin .xlsx)
 * @param {string} [cfg.sheetName='Hoja 1']
 * @param {string} [cfg.title] - Título opcional arriba del header
 */
export async function exportWithSchema({
  rows,
  visibleColumns,
  schema,
  filename,
  sheetName = 'Hoja 1',
  title
}) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(sheetName);

  // Encabezados
  const headers = visibleColumns.map((key) => ({
    header: schema[key]?.header ?? key,
    key,
    width: 10,
  }));
  ws.columns = headers;

  // Título (fila 1) si aplica
  let headerRowIndex = 1;
  if (title) {
    ws.insertRow(1, []);
    ws.mergeCells(1, 1, 1, headers.length);
    const tcell = ws.getCell(1, 1);
    tcell.value = title;
    tcell.font = { bold: true, size: 15 };
    tcell.alignment = { horizontal: 'left', vertical: 'middle' };
    tcell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFF6FF' } }; // azulito suave
    ws.getRow(1).height = 22;
    headerRowIndex = 2;
  }

  // Estilo encabezado
  const headerRow = ws.getRow(headerRowIndex);
  headerRow.font = { bold: true };
  headerRow.alignment = { vertical: 'middle' };
  headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }; // gris 100
  ws.autoFilter = {
    from: { row: headerRowIndex, column: 1 },
    to: { row: headerRowIndex, column: headers.length },
  };
  ws.views = [{ state: 'frozen', ySplit: headerRowIndex }];

  // Utils de valores
  const asDashIfEmpty = (v) => (v === null || v === undefined || v === '' ? '-' : v);
  const asDate = (v) => {
    if (!v) return null;
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
    if (typeof v === 'string') {
      const ddmm = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(v);
      if (ddmm) {
        const d = new Date(parseInt(ddmm[3], 10), parseInt(ddmm[2], 10) - 1, parseInt(ddmm[1], 10));
        return isNaN(d.getTime()) ? null : d;
      }
    }
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };

  const asDateLocalForExcel = (v) => {
    const d = asDate(v);
    if (!d) return null;
    return new Date(
      Date.UTC(
        d.getFullYear(),
        d.getMonth(),
        d.getDate(),
        d.getHours(),
        d.getMinutes(),
        d.getSeconds()
      )
    );
  };

  // Filas
  rows.forEach((rowData) => {
    // 1) Armar valores
    const rowValues = {};
    visibleColumns.forEach((key) => {
      const col = schema[key] || {};
      const raw = typeof col.value === 'function' ? col.value(rowData) : rowData[key];

      if (col.type === 'boolean') {
        rowValues[key] = raw === true ? 'Sí' : raw === false ? 'No' : '-';
      } else if (col.type === 'datetime') {
        const d = asDateLocalForExcel(raw);
        rowValues[key] = d || '-';
      } else if (col.type === 'date') {
        const d = asDate(raw);
        rowValues[key] = d || '-';
      } else {
        rowValues[key] = asDashIfEmpty(raw);
      }
    });

    // 2) Agregar fila
    const added = ws.addRow(rowValues);
    const rowStyle = getStatusExcelStyle(rowData?.estatus); // estilo por estatus de esta fila

    // 3) Estilos por celda
    visibleColumns.forEach((key, idx) => {
      const col = schema[key] || {};
      const cell = added.getCell(idx + 1);

      if (col.type === 'boolean') {
        if (cell.value === 'Sí') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F4EA' } }; // verde suave
          cell.font = { color: { argb: 'FF166534' }, bold: true };
          cell.alignment = { horizontal: 'center' };
        } else if (cell.value === 'No') {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } }; // rojo suave
          cell.font = { color: { argb: 'FF991B1B' }, bold: true };
          cell.alignment = { horizontal: 'center' };
        } else {
          cell.alignment = { horizontal: 'center' };
        }
      }

      // Fechas
      if (col.type === 'date') cell.numFmt = 'dd/mm/yyyy';
      if (col.type === 'datetime') cell.numFmt = 'dd/mm/yyyy hh:mm';

      if (rowStyle && key !== 'detectado_rfid') {
        cell.fill = rowStyle.fill;
      }
      if (rowStyle && key === 'estatus') {
        if (rowStyle.font) cell.font = { ...(cell.font || {}), ...rowStyle.font };
        if (rowStyle.align) cell.alignment = { ...(cell.alignment || {}), ...rowStyle.align };
      }
    });
  });

  ws.columns.forEach((col) => {
    let max = String(col.header ?? '').length;

    col.eachCell({ includeEmpty: false }, (cell) => {
      if (cell.row < headerRowIndex) return;
      if (cell.isMerged) return;

      const v = cell.value ?? '';
      const str = v instanceof Date
        ? (cell.numFmt && cell.numFmt.includes('h') ? '00/00/0000 00:00' : '00/00/0000')
        : String(v);

      max = Math.max(max, str.length);
    });

    col.width = max + COL_MARGIN;
  });

  // 5) Descargar
  const buf = await wb.xlsx.writeBuffer();
  saveAs(
    new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${filename}.xlsx`
  );
}